import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Icon, type IconName } from './icons.tsx';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

/* ---------- Button ---------- */
/** `loading` keeps the button focusable (aria-disabled) so keyboard focus is not dropped mid-action; clicks are ignored while loading. */
export function Button({ variant = 'secondary', size = 'md', loading, icon, children, className, disabled, type = 'button', onClick, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'sm' | 'md'; loading?: boolean; icon?: IconName }) {
  return (
    <button type={type} className={cx('ag-btn', `ag-btn--${variant}`, size === 'sm' && 'ag-btn--sm', className)} disabled={disabled} aria-disabled={loading || undefined} aria-busy={loading || undefined} onClick={loading ? (e) => e.preventDefault() : onClick} {...rest}>
      {loading ? <span className="ag-spinner" aria-hidden="true" /> : icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}
export function IconButton({ icon, label, className, size = 16, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; size?: 16 | 20 }) {
  return (
    <button type="button" aria-label={label} title={label} className={cx('ag-iconbtn', className)} {...rest}>
      <Icon name={icon} size={size} />
    </button>
  );
}

/* ---------- Fields ---------- */
type FieldShell = { label: string; hint?: string; error?: string; required?: boolean };
function Shell({ id, label, hint, error, required, children }: FieldShell & { id: string; children: ReactNode }) {
  return (
    <div className="ag-field">
      <label htmlFor={id} className="ag-label">
        {label}
        {required ? <span className="ag-req" aria-hidden="true"> *</span> : null}
      </label>
      {children}
      {hint && !error ? <p id={`${id}-hint`} className="ag-hint">{hint}</p> : null}
      {error ? <p id={`${id}-err`} className="ag-error-text" role="alert"><Icon name="alert" size={16} /> {error}</p> : null}
    </div>
  );
}
const describe = (id: string, hint?: string, error?: string) => (error ? `${id}-err` : hint ? `${id}-hint` : undefined);

export function TextField({ label, hint, error, required, className, id: idProp, ...rest }: FieldShell & InputHTMLAttributes<HTMLInputElement>) {
  const auto = useId();
  const id = idProp ?? auto;
  return (
    <Shell id={id} label={label} hint={hint} error={error} required={required}>
      <input id={id} className={cx('ag-input', error && 'ag-input--error', className)} aria-invalid={error ? true : undefined} aria-describedby={describe(id, hint, error)} aria-required={required || undefined} {...rest} />
    </Shell>
  );
}
export function TextAreaField({ label, hint, error, required, className, id: idProp, ...rest }: FieldShell & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const auto = useId();
  const id = idProp ?? auto;
  return (
    <Shell id={id} label={label} hint={hint} error={error} required={required}>
      <textarea id={id} className={cx('ag-input ag-textarea', error && 'ag-input--error', className)} aria-invalid={error ? true : undefined} aria-describedby={describe(id, hint, error)} aria-required={required || undefined} {...rest} />
    </Shell>
  );
}
export function SelectField({ label, hint, error, required, className, id: idProp, children, ...rest }: FieldShell & SelectHTMLAttributes<HTMLSelectElement>) {
  const auto = useId();
  const id = idProp ?? auto;
  return (
    <Shell id={id} label={label} hint={hint} error={error} required={required}>
      <select id={id} className={cx('ag-input ag-select', error && 'ag-input--error', className)} aria-invalid={error ? true : undefined} aria-describedby={describe(id, hint, error)} {...rest}>
        {children}
      </select>
    </Shell>
  );
}

/* ---------- Badge ---------- */
export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'error' | 'info';
const toneIcon: Partial<Record<Tone, IconName>> = { success: 'check', warning: 'alert', error: 'alert', info: 'info' };
/** Status is always text; tone adds an icon so colour is never the only signal. */
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  const i = toneIcon[tone];
  return (
    <span className={cx('ag-badge', `ag-badge--${tone}`)}>
      {i ? <Icon name={i} size={16} /> : <span className="ag-badge__dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

/* ---------- Loading / empty / error ---------- */
export function Skeleton({ width, height = 14, className }: { width?: number | string; height?: number | string; className?: string }) {
  return <span className={cx('ag-skeleton', className)} style={{ width, height }} aria-hidden="true" />;
}
export function EmptyState({ title, children, action, icon = 'inbox' }: { title: string; children?: ReactNode; action?: ReactNode; icon?: IconName }) {
  return (
    <div className="ag-state">
      <Icon name={icon} size={24} />
      <h2 className="ag-state__title">{title}</h2>
      {children ? <p className="ag-state__body">{children}</p> : null}
      {action}
    </div>
  );
}
export function ErrorState({ title = 'Something went wrong', message, requestId, onRetry }: { title?: string; message: string; requestId?: string; onRetry?: () => void }) {
  return (
    <div className="ag-state ag-state--error" role="alert">
      <Icon name="alert" size={24} />
      <h2 className="ag-state__title">{title}</h2>
      <p className="ag-state__body">{message}</p>
      {requestId ? <p className="ag-mono ag-state__ref">Request ID: {requestId}</p> : null}
      {onRetry ? <Button onClick={onRetry} icon="refresh">Try again</Button> : null}
    </div>
  );
}
/** `live={false}` when another region already announces the same event (avoids double announcements). */
export function Notice({ tone = 'info', title, children, live = true }: { tone?: 'info' | 'success' | 'warning' | 'error'; title?: string; children: ReactNode; live?: boolean }) {
  return (
    <div className={cx('ag-notice', `ag-notice--${tone}`)} role={live ? (tone === 'error' ? 'alert' : 'status') : undefined}>
      <Icon name={tone === 'success' ? 'check' : tone === 'info' ? 'info' : 'alert'} size={20} />
      <div>
        {title ? <strong>{title}</strong> : null}
        <div>{children}</div>
      </div>
    </div>
  );
}

/* ---------- Drawer (modal, focus-managed) ---------- */
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
export function Drawer({ open, onClose, title, children, footer, width = 480 }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; width?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const returnTo = useRef<Element | null>(null);
  // Keep the latest onClose without re-running the focus effect on every parent render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement;
    const el = ref.current!;
    (el.querySelector<HTMLElement>('[data-autofocus]') ?? el).focus();
    const items = () => [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((i) => i.getAttribute('aria-disabled') !== 'true');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
      } else if (e.key === 'Tab') {
        const list = items();
        if (!list.length) { e.preventDefault(); el.focus(); return; }
        const first = list[0]!;
        const last = list[list.length - 1]!;
        const active = document.activeElement;
        // From the container itself or outside the dialog, always re-enter at the correct end.
        if (e.shiftKey && (active === first || active === el || !el.contains(active))) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (active === last || !el.contains(active))) { e.preventDefault(); first.focus(); }
      }
    };
    // If the focused control disappears (removed or disabled), focus falls to <body>: pull it back into the dialog.
    const onFocusOut = () => requestAnimationFrame(() => { if (document.activeElement === document.body || (document.activeElement && !el.contains(document.activeElement) && !(document.activeElement as HTMLElement).closest('.ag-toasts'))) el.focus(); });
    document.addEventListener('keydown', onKey);
    el.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('keydown', onKey);
      el.removeEventListener('focusout', onFocusOut);
      (returnTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className="ag-drawer-root">
      <div className="ag-scrim" onClick={onClose} aria-hidden="true" />
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="ag-drawer" style={{ ['--drawer-w' as string]: `${width}px` }}>
        <header className="ag-drawer__head">
          <h2 id={titleId} className="ag-drawer__title">{title}</h2>
          <IconButton icon="x" label="Close panel" onClick={onClose} />
        </header>
        <div className="ag-drawer__body">{children}</div>
        {footer ? <footer className="ag-drawer__foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

/* ---------- Tabs ---------- */
/** `id` namespaces the tabs; render the active panel as `<div role="tabpanel" id={tabPanelId(id)} aria-labelledby={tabId(id, value)}>`. */
export function Tabs<T extends string>({ id: base, tabs, value, onChange, label }: { id: string; tabs: { id: T; label: string }[]; value: T; onChange: (v: T) => void; label: string }) {
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const target = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : e.key === 'ArrowRight' ? (i + 1) % tabs.length : e.key === 'ArrowLeft' ? (i - 1 + tabs.length) % tabs.length : -1;
    if (target < 0) return;
    e.preventDefault();
    const next = tabs[target]!;
    onChange(next.id);
    document.getElementById(`${base}-${next.id}`)?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className="ag-tabs">
      {tabs.map((t, i) => (
        <button key={t.id} id={`${base}-${t.id}`} role="tab" type="button" aria-selected={t.id === value} aria-controls={`${base}-panel`} tabIndex={t.id === value ? 0 : -1} className="ag-tab" onClick={() => onChange(t.id)} onKeyDown={(e) => onKey(e, i)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}
export const tabPanelId = (base: string) => `${base}-panel`;
export const tabId = (base: string, tab: string) => `${base}-${tab}`;

/* ---------- Segmented control (e.g. device, theme) ---------- */
export function Segmented<T extends string>({ options, value, onChange, label }: { options: { id: T; label: string; icon?: IconName }[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div role="group" aria-label={label} className="ag-seg">
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={o.id === value} aria-label={o.icon ? o.label : undefined} title={o.label} className="ag-seg__btn" onClick={() => onChange(o.id)}>
          {o.icon ? <Icon name={o.icon} /> : o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- Toasts ---------- */
type ToastItem = { id: number; tone: 'success' | 'error' | 'info'; message: string };
const ToastCtx = createContext<{ push: (t: Omit<ToastItem, 'id'>) => void } | null>(null);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((l) => l.filter((t) => t.id !== id)), []);
  const [announce, setAnnounce] = useState<{ n: number; tone: ToastItem['tone']; message: string } | null>(null);
  const push = useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = ++seq.current;
    setItems((l) => [...l.slice(-3), { ...t, id }]);
    setAnnounce({ n: id, tone: t.tone, message: t.message });
    // Success/info fade after a while (long enough to read); errors stay until dismissed.
    if (t.tone !== 'error') setTimeout(() => dismiss(id), 8000);
  }, [dismiss]);
  const value = useMemo(() => ({ push }), [push]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      {/* Screen-reader announcements live in one stable region; the visual toasts are not live, so nothing is read twice. */}
      <div className="ag-visually-hidden" role={announce?.tone === 'error' ? 'alert' : 'status'} key={announce?.tone === 'error' ? 'alert' : 'status'}>{announce?.message}</div>
      <div className="ag-toasts" role="region" aria-label="Notifications">
        {items.map((t) => (
          <div key={t.id} className={cx('ag-toast', `ag-toast--${t.tone}`)}>
            <Icon name={t.tone === 'success' ? 'check' : t.tone === 'error' ? 'alert' : 'info'} size={20} />
            <span>{t.message}</span>
            <IconButton icon="x" label="Dismiss notification" onClick={() => dismiss(t.id)} />
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export function useToast() {
  const c = useContext(ToastCtx);
  if (!c) throw new Error('useToast must be used inside ToastProvider');
  return c.push;
}

/* ---------- Theme (admin) ---------- */
export type ThemePref = 'light' | 'dark' | 'system';
const KEY = 'agency-admin-theme';
export function resolveTheme(pref: ThemePref): 'admin-light' | 'admin-dark' {
  const dark = pref === 'dark' || (pref === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  return dark ? 'admin-dark' : 'admin-light';
}
export function applyStoredTheme() {
  const pref = (localStorage.getItem(KEY) as ThemePref | null) ?? 'system';
  document.documentElement.dataset.theme = resolveTheme(pref);
}
export function useThemePreference(): [ThemePref, (p: ThemePref) => void] {
  const [pref, setPref] = useState<ThemePref>(() => (localStorage.getItem(KEY) as ThemePref | null) ?? 'system');
  useEffect(() => {
    localStorage.setItem(KEY, pref);
    document.documentElement.dataset.theme = resolveTheme(pref);
    if (pref !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => (document.documentElement.dataset.theme = resolveTheme('system'));
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [pref]);
  return [pref, setPref];
}

/** Props for a horizontally scrollable region (wide tables): keyboard users must be able to focus it to scroll (WCAG 2.1.1). */
export const scrollRegion = (label: string) => ({ tabIndex: 0, role: 'region' as const, 'aria-label': label });
